import { useEffect, useMemo, useState } from 'react';
import { useOutletContext, useParams, Link } from 'react-router-dom';
import type { Rahmen } from './Projektrahmen';
import { ebeneMitHaus } from '../mehrhaus';
import type {
  ApiClient, Tuer, TuerDetail, Tuerfeld, TuerbuchDaten, Tuertyp, ExcelVorschau,
  TuerSicherheitDetail,
} from '../api';
import { Fachaktionen } from './Fachaktionen';
import { Legende, farbklasse } from '../Farbregel';
import {
  IconEbene, IconRaum, IconTuer, IconTuertyp, IconSuche, IconTabelle,
  IconHochladen, IconPdf, GruppenIcon,
} from '../Icons';
import {
  sicherheitStatus, sicherungsseite, raumVerfahren, tuerState, jaNein, merkmal,
} from '../tuerKlartext';
import { GewerkUnterlagenZip } from '../UnterlagenZipKnopf';
import { PageHeader } from '../rahmen/PageHeader';
import { Tueranforderungshinweis } from './Tueranforderungshinweis';
import {
  ContentSection, DataTable, Drawer, EmptyState, ErrorState,
  Feld as MusterFeld, KpiCard, KpiReihe, LoadingState, StatusBadge,
  formatiereZahl, leseZahl, type Spalte, type Zustand,
} from '../muster';

/**
 * Digitales Türbuch.
 *
 * Links die Gliederung über drei Stufen: Geschoss, Raum, Tür. Eine Stufe mehr
 * als beim Raumbuch, weil eine Tür zu einem Raum gehört - und bei tausend Türen
 * über achthundert Räume wäre eine flache Liste nicht benutzbar.
 *
 * Rechts die gewählte Tür mit allen Werten nach Fachgruppen.
 */
export function Tuerbuch({ api }: { api: ApiClient }) {
  const { id: projektId } = useParams<{ id: string }>();
  // F60 H2b: die Haus-Wahl des Projektrahmens (null = alle; ohne Rahmen bzw. bei einem Haus immer null).
  const haus = useOutletContext<Rahmen | null | undefined>()?.haus ?? null;

  const [daten, setDaten] = useState<TuerbuchDaten | null>(null);
  const [felder, setFelder] = useState<Tuerfeld[] | null>(null);
  const [fehler, setFehler] = useState<unknown>(null);

  const [offeneEbenen, setOffeneEbenen] = useState<Set<string>>(new Set());
  const [offeneRaeume, setOffeneRaeume] = useState<Set<string>>(new Set());
  const [gewaehlt, setGewaehlt] = useState<string | null>(null);
  const [suche, setSuche] = useState('');
  const [einlesen, setEinlesen] = useState<File | null>(null);

  // Die Filter der Spaltengruppe „Sicherheit".
  //
  // Vier statt einer Freitextsuche: Nach „Zonenwechsel" sucht man nicht als
  // Wort, sondern als Eigenschaft — und „alle Türen mit einem offenen Punkt"
  // ist die Frage, mit der ein Planer an das Türbuch geht.
  const [nurZonenwechsel, setNurZonenwechsel] = useState(false);
  const [filterKlasse, setFilterKlasse] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterOp, setFilterOp] = useState('');

  const laden = async () => {
    if (!projektId) return;
    try {
      // Ohne Haus-Wahl genau der bisherige Aufruf (ein Argument).
      const [d, f] = await Promise.all([
        haus == null ? api.tueren(projektId) : api.tueren(projektId, haus),
        api.tuerfelder(),
      ]);
      setDaten(d);
      setFelder(f);
      setFehler(null);

      if (d.tueren.length > 0 && offeneEbenen.size === 0) {
        const t = d.tueren.find((x) => x.state !== 'entfernt');
        const erste = t?.geschoss
          ? ebeneMitHaus(t.geschoss, t.hausNummer, d.mehrhaus === true) : undefined;
        if (erste) setOffeneEbenen(new Set([erste]));
      }
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    }
  };

  useEffect(() => { laden(); }, [api, projektId, haus]);

  // Gliederung nach Geschoss, darunter nach Raum.
  const ebenen = useMemo(() => {
    if (!daten) return [];

    const treffer = suche.trim().toLowerCase();
    const nachEbene = new Map<string, Map<string, Tuer[]>>();

    for (const t of daten.tueren) {
      if (t.state === 'entfernt') continue;

      // Die Sicherheitsfilter zuerst — sie sind billiger als die Textsuche
      // und schneiden meist mehr weg.
      if (nurZonenwechsel && t.istZonenwechsel !== true) continue;
      if (filterKlasse && t.tuerklasse !== filterKlasse) continue;
      if (filterStatus && t.sicherheitStatus !== filterStatus) continue;

      if (filterOp
          && !(t.sicherheitOffenePunkte ?? '').includes(filterOp)) continue;

      if (treffer) {
        const passt = (t.tuernummer ?? '').toLowerCase().includes(treffer)
                   || (t.raumname ?? '').toLowerCase().includes(treffer)
                   || (t.tuertyp ?? '').toLowerCase().includes(treffer)
                   || (t.anforderungscode ?? '').toLowerCase().includes(treffer)
                   || (t.anforderungsstatus ?? '').toLowerCase().includes(treffer)
                   || (t.tuerklasse ?? '').toLowerCase().includes(treffer)
                   || (t.schutzzoneBandseite ?? '').toLowerCase().includes(treffer)
                   || (t.schutzzoneBandgegenseite ?? '')
                        .toLowerCase().includes(treffer)
                   || (t.sicherheitOffenePunkte ?? '')
                        .toLowerCase().includes(treffer)
                   || t.geschoss.toLowerCase().includes(treffer);
        if (!passt) continue;
      }

      // Das abgeleitete Geschoss, nicht die Revit-Ebene: E0, E1, E2 - so
      // denkt die Fachplanung, und so steht es in den Raumnummern.
      // F60 H2b: im Mehrhausprojekt „Haus 02 · E0“ - Haus vor Geschoss.
      const ebene = ebeneMitHaus(t.geschoss || 'ohne Geschoss', t.hausNummer, daten.mehrhaus === true);
      const raum = t.raumnummer ?? 'ohne Raum';

      if (!nachEbene.has(ebene)) nachEbene.set(ebene, new Map());
      const raeume = nachEbene.get(ebene)!;

      if (!raeume.has(raum)) raeume.set(raum, []);
      raeume.get(raum)!.push(t);
    }

    return [...nachEbene.entries()]
      .map(([name, raeume]) => ({
        name,
        anzahl: [...raeume.values()].reduce((s, l) => s + l.length, 0),
        raeume: [...raeume.entries()]
          .map(([nummer, tueren]) => ({
            nummer,
            name: tueren[0]?.raumname ?? null,
            tueren: tueren.sort((a, b) => (a.tuerImRaum ?? 0) - (b.tuerImRaum ?? 0)),
          }))
          .sort((a, b) => a.nummer.localeCompare(b.nummer, 'de')),
      }))
      .sort((a, b) => a.name.localeCompare(b.name, 'de'));
  }, [daten, suche, nurZonenwechsel, filterKlasse, filterStatus, filterOp]);

  // Die Werte, die es im Bestand überhaupt gibt — als Auswahl statt als
  // Freitext. Ein Filter auf einen Wert, den keine Tür trägt, liefert eine
  // leere Liste und sieht aus wie ein Fehler.
  const sicherheitswerte = useMemo(() => {
    const klassen = new Set<string>();
    const stati = new Set<string>();
    const ops = new Set<string>();

    for (const t of daten?.tueren ?? []) {
      if (t.state === 'entfernt') continue;
      if (t.tuerklasse) klassen.add(t.tuerklasse);
      if (t.sicherheitStatus) stati.add(t.sicherheitStatus);

      for (const op of (t.sicherheitOffenePunkte ?? '')
             .split(',').map((x) => x.trim()).filter(Boolean))
        ops.add(op);
    }

    return {
      klassen: [...klassen].sort(),
      stati: [...stati].sort(),
      ops: [...ops].sort(),
    };
  }, [daten]);

  // Die Werte der gewählten Tür einzeln nachladen. Alle im Voraus zu holen
  // wären bei 1845 Türen fast zwei Megabyte.
  const [detail, setDetail] = useState<TuerDetail | null>(null);
  const [detailLaeuft, setDetailLaeuft] = useState(false);

  useEffect(() => {
    if (!gewaehlt) { setDetail(null); return; }

    setDetailLaeuft(true);
    api.tuer(gewaehlt)
      .then(setDetail)
      .catch((e) => setFehler(e instanceof Error ? e.message : String(e)))
      .finally(() => setDetailLaeuft(false));
  }, [api, gewaehlt]);

  const kopf = (
    <PageHeader
      titel="Türbuch"
      beschreibung={'Links die Gliederung über drei Stufen: Geschoss, Raum, Tür. Eine '
        + 'Stufe mehr als beim Raumbuch, weil eine Tür zu einem Raum gehört — und bei '
        + 'tausend Türen über achthundert Räume wäre eine flache Liste nicht '
        + 'benutzbar. Rechts die gewählte Tür mit allen Werten nach Fachgruppen.'}
      meta={daten
        ? `${daten.tueren.filter((t) => t.state !== 'entfernt').length} Türen`
        : undefined}
    />
  );

  if (fehler && (!daten || !felder)) {
    return (
      <>
        {kopf}
        <ErrorState
          meldung="Das Türbuch konnte nicht geladen werden."
          technisch={fehler}
          beiErneutVersuchen={laden}
        />
      </>
    );
  }

  if (!daten || !felder) {
    return (
      <>
        {kopf}
        <LoadingState text="Türen werden geladen …" zeilen={8} spalten={3} />
      </>
    );
  }

  if (daten.tueren.length === 0) {
    return (
      <>
        {kopf}
        <Fachaktionen api={api} rolleKey="tueren" leseArt={20} schreibArt={21} />
        <EmptyState
          icon={<IconTuer />}
          titel="Noch keine Türen aufgenommen."
          erklaerung={'Die Türen entstehen aus einem Auftrag mit Türaufnahme. Gelesen '
            + 'wird die Familie TuV_E_0000_Türinformationen im Modell, das als '
            + 'Türdatenquelle markiert ist.'}
        />
      </>
    );
  }

  const gesamt = daten.tueren.filter((t) => t.state !== 'entfernt').length;

  return (
    <>
      {kopf}

      {fehler !== null && (
        <ErrorState klein meldung="Die letzte Aktion ist fehlgeschlagen."
                    technisch={fehler} />
      )}

      {/* Lesen und Schreiben im Kopf, Excel und Tuertypen als weitere Knoepfe -
          so liegt alles, was den ganzen Bestand betrifft, an einer Stelle. */}
      <Fachaktionen api={api} rolleKey="tueren" leseArt={20} schreibArt={21}>
          <button className="m-knopf"
                  onClick={() => api.tuerbuchExcel(projektId!, 'Tuerbuch.xlsx')}>
            <IconTabelle size={16} />Als Tabelle
          </button>

          <button className="m-knopf"
                  onClick={() => api.tuerbuchPdf(projektId!, 'Tuerliste.pdf')}>
            <IconPdf size={16} />Detaillierte Türliste
          </button>

          <GewerkUnterlagenZip api={api} projektId={projektId!} gewerk="tueren" />

          <label className="m-knopf" style={{ cursor: 'pointer' }}>
            <IconHochladen size={16} />Tabelle einlesen
            <input
              type="file"
              accept=".xlsx"
              style={{ display: 'none' }}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) setEinlesen(f);
                e.target.value = '';
              }}
            />
          </label>

          <Link to={`/projekte/${projektId}/tuertypen`} className="m-knopf" style={{ textDecoration: 'none' }}>
            <IconTuertyp size={16} />Türtypen
          </Link>
      </Fachaktionen>

      {/* TR3: Stand der Türanforderungen gegen Raum- und Türaufnahme, Vorbehalt R07 (RTA-10). */}
      <Tueranforderungshinweis api={api} projektId={projektId!} />

      <Kennzahlen ebenen={ebenen} gesamt={gesamt} />

      <Sicherheitsfilter
        werte={sicherheitswerte}
        nurZonenwechsel={nurZonenwechsel}
        setNurZonenwechsel={setNurZonenwechsel}
        klasse={filterKlasse}
        setKlasse={setFilterKlasse}
        status={filterStatus}
        setStatus={setFilterStatus}
        op={filterOp}
        setOp={setFilterOp}
        gefunden={gesamt}
      />

      {/* Links die Gliederung, rechts die gewählte Tür - dasselbe Muster wie
          Heizung und Kälte, über die gemeinsamen Klassen .baum, .baum-ebene
          und .baum-zeile. Vorher eine eigene Karte mit von Hand gesetzten
          Farbwerten: ein festes Hellblau für die gewählte Zeile und drei
          Grautöne für die Trennlinien der drei Ebenen. Keiner davon folgte
          einer Themenvariablen. */}
      <div className="karte" style={{ display: 'flex', gap: 0, padding: 0 }}>
        <Navigation
          ebenen={ebenen}
          gesamt={gesamt}
          offeneEbenen={offeneEbenen}
          offeneRaeume={offeneRaeume}
          umschaltenEbene={(e) => {
            const neu = new Set(offeneEbenen);
            if (neu.has(e)) neu.delete(e); else neu.add(e);
            setOffeneEbenen(neu);
          }}
          umschaltenRaum={(r) => {
            const neu = new Set(offeneRaeume);
            if (neu.has(r)) neu.delete(r); else neu.add(r);
            setOffeneRaeume(neu);
          }}
          gewaehlt={gewaehlt}
          waehlen={setGewaehlt}
          suche={suche}
          setSuche={setSuche}
        />

        <div style={{ flex: 1, padding: '12px 16px', minWidth: 0 }}>
          {detail
            ? <Tuerdetail
                key={detail.id}
                api={api}
                projektId={projektId!}
                tuer={detail}
                felder={felder}
                vorlagen={daten.vorlagen}
                neuLaden={async () => {
                  await laden();
                  if (gewaehlt) setDetail(await api.tuer(gewaehlt));
                }}
              />
            : detailLaeuft
              ? <LoadingState text="Tür wird geladen …" zeilen={6} spalten={2} />
              : (
                <EmptyState
                  icon={<IconTuer />}
                  titel="Keine Tür gewählt."
                  erklaerung={'Links ein Geschoss aufklappen, dann einen Raum und eine '
                    + 'Tür wählen — ihre Werte erscheinen dann hier.'}
                />
              )}
        </div>
      </div>

      {einlesen && (
        <ExcelVorschauDialog
          api={api}
          projektId={projektId!}
          datei={einlesen}
          schliessen={() => setEinlesen(null)}
          fertig={() => { setEinlesen(null); laden(); }}
        />
      )}
    </>
  );
}

/**
 * Zeigt vor dem Einlesen, was sich ändern würde.
 *
 * Wie beim Raumbuch: Die Datei war zwischenzeitlich in fremden Händen, jemand
 * hat vielleicht eine Spalte verschoben oder in ein Zahlenfeld „ca. 3" getippt.
 */
function ExcelVorschauDialog({
  api, projektId, datei, schliessen, fertig,
}: {
  api: ApiClient;
  projektId: string;
  datei: File;
  schliessen: () => void;
  fertig: () => void;
}) {
  const [vorschau, setVorschau] = useState<ExcelVorschau | null>(null);
  const [fehler, setFehler] = useState<unknown>(null);
  const [laeuft, setLaeuft] = useState(false);

  useEffect(() => {
    api.tuerbuchEinlesen(projektId, datei, false)
      .then((e) => setVorschau(e.vorschau))
      .catch(setFehler);
  }, [api, projektId, datei]);

  const uebernehmen = async () => {
    setLaeuft(true);
    try {
      await api.tuerbuchEinlesen(projektId, datei, true);
      fertig();
    } catch (e) {
      setFehler(e);
      setLaeuft(false);
    }
  };

  /* Die Vorschau im SCHUBFACH statt im Modal (Abschnitt 16): Sie zeigt eine
     Tabelle, und die war vorher bei 60 Zeilen abgeschnitten — `DataTable`
     virtualisiert stattdessen. */
  return (
    <Drawer
      offen
      titel="Was sich ändern würde"
      beschreibung={vorschau
        ? `${vorschau.zeilenGelesen} Zeilen gelesen · ${vorschau.aenderungen.length} Änderungen`
        : undefined}
      breite="860px"
      beiSchliessen={schliessen}
      fuss={(
        <>
          <button className="m-knopf" onClick={schliessen} disabled={laeuft}>
            Abbrechen
          </button>
          <button
            className="m-knopf m-knopf-haupt"
            onClick={uebernehmen}
            disabled={laeuft || !vorschau || vorschau.aenderungen.length === 0}
          >
            {laeuft
              ? 'wird übernommen …'
              : `${vorschau?.aenderungen.length ?? 0} übernehmen`}
          </button>
        </>
      )}
    >

        {fehler !== null && (
          <ErrorState
            meldung="Die Tabelle konnte nicht gelesen werden."
            technisch={fehler}
          />
        )}

        {!vorschau && fehler === null && (
          <LoadingState text="Tabelle wird geprüft …" zeilen={5} spalten={4} />
        )}

        {vorschau && (
          <>
            <KpiReihe>
              <KpiCard titel="Zeilen gelesen" wert={vorschau.zeilenGelesen} />
              <KpiCard titel="Änderungen" wert={vorschau.aenderungen.length} />
              <KpiCard titel="Unverändert" wert={vorschau.unveraendert} />
              {vorschau.nichtZuordenbar.length > 0 && (
                <KpiCard
                  titel="Nicht zuordenbar" wert={vorschau.nichtZuordenbar.length}
                  zustand="fehler" zustandText="ohne Kennung oder unbekannt"
                />
              )}
            </KpiReihe>

            {vorschau.nichtZuordenbar.length > 0 && (
              <div className="hinweis warnung">
                <span className="hinweis-zeichen">!</span>
                <div>
                  <strong>Ohne Kennung oder unbekannt:</strong>{' '}
                  {vorschau.nichtZuordenbar.slice(0, 8).join(', ')}
                  {vorschau.nichtZuordenbar.length > 8 && ' …'}
                  <br />
                  <span className="sehr-schwach">
                    Die verborgene erste Spalte darf nicht gelöscht werden.
                  </span>
                </div>
              </div>
            )}

            {vorschau.auffaelligkeiten.length > 0 && (
              <div className="hinweis warnung">
                <span className="hinweis-zeichen">!</span>
                <div>
                  <strong>Nicht als Zahl lesbar:</strong>
                  <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                    {vorschau.auffaelligkeiten.slice(0, 6).map((a2, i) => (
                      <li key={i} style={{ fontSize: 12 }}>{a2}</li>
                    ))}
                  </ul>
                  <span className="sehr-schwach">Diese Werte werden übersprungen.</span>
                </div>
              </div>
            )}

            <DataTable
              name="Änderungen"
              spalten={[
                {
                  schluessel: 'tuer', titel: 'Tür', breite: '130px',
                  immerSichtbar: true,
                  wert: (a2: ExcelVorschau['aenderungen'][number]) => a2.nummer,
                  inhalt: (a2) => <strong>{a2.nummer}</strong>,
                },
                {
                  schluessel: 'feld', titel: 'Feld',
                  wert: (a2: ExcelVorschau['aenderungen'][number]) => a2.feld,
                },
                {
                  schluessel: 'alt', titel: 'bisher',
                  wert: (a2: ExcelVorschau['aenderungen'][number]) => a2.alt ?? '',
                  inhalt: (a2) => <span className="sehr-schwach">{a2.alt ?? '—'}</span>,
                },
                {
                  schluessel: 'neu', titel: 'neu',
                  wert: (a2: ExcelVorschau['aenderungen'][number]) => a2.neu ?? '',
                  inhalt: (a2) => a2.neu
                    ?? <span className="sehr-schwach">gelöscht</span>,
                },
              ]}
              zeilen={vorschau.aenderungen}
              schluessel={(a2) => `${a2.nummer}|${a2.feld}`}
              hoehe={420}
              dicht
              leer={(
                <EmptyState
                  titel="Keine Änderung."
                  erklaerung="Die Tabelle stimmt mit dem Türbuch überein."
                />
              )}
            />
          </>
        )}
      </Drawer>
  );
}

// --- Navigation ---------------------------------------------------------------

// --- Kennzahlen --------------------------------------------------------------

/**
 * Die Zahlen über dem Baum.
 *
 * Gepflegt und zugeordnet sind die beiden Fragen, die das Türbuch beantwortet:
 * ob die Werte stehen und ob die Anforderung sitzt. Beide sind je Tür ein
 * Punkt oder ein Ausrufezeichen im Baum - über 1.845 Türen sagt erst die Summe
 * etwas.
 *
 * "neu" steht daneben, weil die Marke im Baum bis zum 20.08.2026 an JEDER Tür
 * klebte: Der Türbuchimport setzte den Zustand 'bekannt' nie, obwohl alle
 * Türen längst wiedergefunden waren. Eine Marke, die überall steht, sagt
 * nichts - und an dieser Zahl fällt es auf.
 */
/**
 * Die Filter der Spaltengruppe „Sicherheit".
 *
 * ══════════════════════════════════════════════════════════════════════════
 * WARUM VIER FILTER UND KEIN FREITEXT
 * ══════════════════════════════════════════════════════════════════════════
 *
 * Weil man nach „Zonenwechsel" nicht als Wort sucht, sondern als Eigenschaft.
 * Und weil die Frage, mit der ein Planer an das Türbuch geht, lautet: „Welche
 * Türen haben einen offenen Punkt?" — nicht „welche Tür enthält OP-05 in einem
 * Textfeld".
 *
 * Die Auswahllisten kommen aus dem BESTAND und nicht aus einer festen Liste:
 * Ein Filter auf einen Wert, den keine Tür trägt, liefert eine leere Liste und
 * sieht aus wie ein Fehler. Ist noch nichts abgeleitet, ist die Leiste leer und
 * sagt das.
 */
function Sicherheitsfilter({
  werte, nurZonenwechsel, setNurZonenwechsel, klasse, setKlasse,
  status, setStatus, op, setOp, gefunden,
}: {
  werte: { klassen: string[]; stati: string[]; ops: string[] };
  nurZonenwechsel: boolean;
  setNurZonenwechsel: (b: boolean) => void;
  klasse: string;
  setKlasse: (s: string) => void;
  status: string;
  setStatus: (s: string) => void;
  op: string;
  setOp: (s: string) => void;
  gefunden: number;
}) {
  const nichtsAbgeleitet = werte.klassen.length === 0
                        && werte.stati.length === 0;

  if (nichtsAbgeleitet) {
    return (
      <p className="sehr-schwach" style={{ fontSize: 12, margin: '0 0 10px' }}>
        Sicherheitskonzept: noch nicht abgeleitet. Die Zonen, Türklassen und
        Anforderungen entstehen mit der Ableitung — sie liest die Schutzzonen aus
        dem Raumbuch und das Regelwerk aus den Stammdaten.
      </p>
    );
  }

  const gefiltert = nurZonenwechsel || klasse || status || op;

  return (
    <ContentSection titel="Sicherheit" nackt>
      <div className="m-feldgitter m-feldgitter-3">
        <MusterFeld
          art="haken"
          beschriftung="nur Zonenwechsel"
          wert={nurZonenwechsel}
          beiAenderung={(w) => setNurZonenwechsel(w as boolean)}
        />
        <MusterFeld
          art="auswahl"
          beschriftung="Türklasse"
          wert={klasse}
          beiAenderung={(w) => setKlasse(w as string)}
          optionen={[
            { wert: '', beschriftung: 'alle' },
            ...werte.klassen.map((k) => ({ wert: k, beschriftung: k })),
          ]}
        />
        <MusterFeld
          art="auswahl"
          beschriftung="Status"
          wert={status}
          beiAenderung={(w) => setStatus(w as string)}
          optionen={[
            { wert: '', beschriftung: 'alle' },
            ...werte.stati.map((x) => ({ wert: x, beschriftung: sicherheitStatus(x) })),
          ]}
        />
        {werte.ops.length > 0 && (
          <MusterFeld
            art="auswahl"
            beschriftung="offener Punkt"
            wert={op}
            beiAenderung={(w) => setOp(w as string)}
            optionen={[
              { wert: '', beschriftung: 'alle' },
              ...werte.ops.map((x) => ({ wert: x, beschriftung: x })),
            ]}
          />
        )}
      </div>

      {gefiltert && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 }}>
          <StatusBadge zustand="info" text={`${gefunden} Türen`} klein />
          <button
            className="m-knopf"
            onClick={() => {
              setNurZonenwechsel(false);
              setKlasse('');
              setStatus('');
              setOp('');
            }}
          >
            Filter zurücksetzen
          </button>
        </div>
      )}
    </ContentSection>
  );
}

function Kennzahlen({ ebenen, gesamt }: { ebenen: Ebene[]; gesamt: number }) {
  const z = useMemo(() => {
    const alle = ebenen.flatMap((e) => e.raeume.flatMap((r) => r.tueren));

    return {
      tueren: gesamt,
      geschosse: ebenen.length,
      gepflegt: alle.filter((t) => t.gepflegt).length,
      pruefen: alle.filter((t) => t.anforderungsstatus === 'pruefen').length,
      neu: alle.filter((t) => t.state === 'neu').length,
      ohneTyp: alle.filter((t) => !t.tuertyp).length,
      // "missing" und "klaerung" - assumption zählt bewusst nicht mit, das
      // ist eine getroffene Annahme und kein offener Vorgang (siehe Punkte
      // im Baum, Navigation).
      sicherheitOffen: alle.filter((t) => t.sicherheitStatus === 'missing'
        || t.sicherheitStatus === 'klaerung').length,
    };
  }, [ebenen, gesamt]);

  const anteil = (n: number) =>
    (z.tueren > 0 ? `${Math.round(100 * n / z.tueren)} % von ${z.tueren}` : undefined);

  return (
    <KpiReihe>
      <KpiCard titel="Türen" wert={z.tueren} vergleich={`${z.geschosse} Geschosse`} />
      <KpiCard titel="Gepflegt" wert={z.gepflegt} vergleich={anteil(z.gepflegt)} />
      <KpiCard
        titel="Anforderung zu prüfen" wert={z.pruefen} vergleich={anteil(z.pruefen)}
        zustand={z.pruefen > 0 ? 'warnung' : undefined}
        zustandText={z.pruefen > 0 ? 'Anforderung sitzt noch nicht' : undefined}
      />
      <KpiCard
        titel="Sicherheit zu prüfen" wert={z.sicherheitOffen}
        vergleich={anteil(z.sicherheitOffen)}
        zustand={z.sicherheitOffen > 0 ? 'warnung' : undefined}
        zustandText={z.sicherheitOffen > 0 ? 'fehlend oder in Klärung' : undefined}
      />
      <KpiCard
        titel="Ohne Türtyp" wert={z.ohneTyp} vergleich={anteil(z.ohneTyp)}
        zustand={z.ohneTyp > 0 ? 'warnung' : undefined}
        zustandText={z.ohneTyp > 0 ? 'keine Vorlage zugeordnet' : undefined}
      />
      <KpiCard
        titel="Noch neu" wert={z.neu} vergleich={anteil(z.neu)}
        fussnote={'Eine Marke, die überall steht, sagt nichts — an dieser Zahl fällt '
          + 'es auf.'}
      />
    </KpiReihe>
  );
}

type Ebene = {
  name: string;
  anzahl: number;
  raeume: { nummer: string; name: string | null; tueren: Tuer[] }[];
};

function Navigation({
  ebenen, gesamt, offeneEbenen, offeneRaeume,
  umschaltenEbene, umschaltenRaum, gewaehlt, waehlen, suche, setSuche,
}: {
  ebenen: Ebene[];
  gesamt: number;
  offeneEbenen: Set<string>;
  offeneRaeume: Set<string>;
  umschaltenEbene: (e: string) => void;
  umschaltenRaum: (r: string) => void;
  gewaehlt: string | null;
  waehlen: (id: string) => void;
  suche: string;
  setSuche: (s: string) => void;
}) {
  return (
    <div className="baum">
      <div style={{ padding: '10px 12px' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <IconSuche size={14} />
          <input
            value={suche}
            onChange={(e) => setSuche(e.target.value)}
            placeholder="Türnummer, Raum oder Typ"
            style={{ width: '100%', padding: '4px 6px', fontSize: 13 }}
          />
        </label>
        <div className="sehr-schwach" style={{ fontSize: 11, marginTop: 6 }}>
          {gesamt.toLocaleString('de-DE')} Türen
        </div>
      </div>

      {ebenen.map((e) => (
        <div key={e.name}>
          {/* Geschoss */}
          <button className="baum-ebene" onClick={() => umschaltenEbene(e.name)}>
            <IconEbene size={14} />
            <span>{e.name}</span>
            <span className="schwach">{e.anzahl}</span>
          </button>

          {offeneEbenen.has(e.name) && e.raeume.map((r) => {
            const schluessel = `${e.name}|${r.nummer}`;

            return (
              <div key={schluessel}>
                {/* Raum - die MITTLERE Ebene. Sie bekommt baum-zeile mit
                    eigener Einrückung, damit sie sich von der Tür darunter
                    unterscheidet, ohne eine vierte Klasse zu brauchen. */}
                <button
                  className="baum-zeile"
                  style={{ paddingLeft: 22 }}
                  onClick={() => umschaltenRaum(schluessel)}
                  title={r.name ?? undefined}
                >
                  <IconRaum size={13} />
                  <span style={{ minWidth: 0, overflow: 'hidden',
                                 textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {r.name ?? r.nummer}
                  </span>
                  <span className="schwach">{r.tueren.length}</span>
                </button>

                {/* Türen */}
                {offeneRaeume.has(schluessel) && r.tueren.map((t) => (
                  <button
                    key={t.id}
                    className={`baum-zeile ${gewaehlt === t.id ? 'aktiv' : ''}`}
                    style={{ paddingLeft: 40 }}
                    onClick={() => waehlen(t.id)}
                    title={`${t.tuernummer ?? ''} ${t.tuertyp ?? ''}`.trim()}
                  >
                    <IconTuer size={12} />
                    <span>{t.tuernummer ?? '—'}</span>

                    <span style={{ marginLeft: 'auto', display: 'flex',
                                   alignItems: 'center', gap: 5, fontSize: 10 }}>
                      {/* Ein Punkt für gepflegte Türen - so ist auf einen Blick
                          zu sehen, wo noch Arbeit liegt. */}
                      {t.gepflegt && (
                        <span title="Werte gepflegt"
                              style={{ width: 5, height: 5, borderRadius: '50%',
                                       background: 'var(--gut)' }} />
                      )}
                      {/* Zwei weitere Punkte fürs Sicherheitskonzept, im selben
                          Stil: blau für einen Zonenwechsel (dort sitzt eine
                          Sicherungsseite), rot für einen Status, dem der
                          Planer noch nachgehen muss - fehlt oder Klärung
                          offen. Assumption bekommt bewusst KEINEN Punkt: Eine
                          Annahme ist eine getroffene Entscheidung, kein
                          offener Vorgang. */}
                      {t.istZonenwechsel === true && (
                        <span title="Zonenwechsel"
                              style={{ width: 5, height: 5, borderRadius: '50%',
                                       background: 'var(--laufend)' }} />
                      )}
                      {(t.sicherheitStatus === 'missing'
                        || t.sicherheitStatus === 'klaerung') && (
                        <span title={`Sicherheitskonzept: ${sicherheitStatus(t.sicherheitStatus)}`}
                              style={{ width: 5, height: 5, borderRadius: '50%',
                                       background: 'var(--schlecht)' }} />
                      )}
                      {t.anforderungsstatus === 'pruefen' && (
                        <StatusBadge
                          klein zustand="warnung" text="prüfen"
                          titel={t.anforderungspruefung
                            ?? 'Anforderungszuordnung prüfen'}
                        />
                      )}
                      {t.tuertyp && (
                        <span className="sehr-schwach">{t.tuertyp}</span>
                      )}
                    </span>
                  </button>
                ))}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

// --- Türdetail ----------------------------------------------------------------

function Tuerdetail({
  api, projektId, tuer, felder, vorlagen, neuLaden,
}: {
  api: ApiClient;
  projektId: string;
  tuer: TuerDetail;
  felder: Tuerfeld[];
  vorlagen: Tuertyp[];
  neuLaden: () => void | Promise<void>;
}) {
  const ausDb: Record<string, string | null> = tuer.dataJson ? JSON.parse(tuer.dataJson) : {};

  const [werte, setWerte] = useState(ausDb);
  const [gespeichert, setGespeichert] = useState(false);
  const [fehler, setFehler] = useState<unknown>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [vorlagenDialog, setVorlagenDialog] = useState(false);

  const geaendert = useMemo(() => {
    const alle = new Set([...Object.keys(ausDb), ...Object.keys(werte)]);
    for (const k of alle) {
      if ((ausDb[k] ?? '') !== (werte[k] ?? '')) return true;
    }
    return false;
  }, [ausDb, werte]);

  const gruppen = useMemo(() => {
    const map = new Map<string, Tuerfeld[]>();
    for (const f of felder) {
      const g = f.gruppe ?? 'Sonstige';
      if (!map.has(g)) map.set(g, []);
      map.get(g)!.push(f);
    }
    return [...map.entries()];
  }, [felder]);

  const vorlage = vorlagen.find((v) => v.templateKey === tuer.tuertyp);

  // Der Türtyp wird SOFORT gespeichert, nicht mit dem Knopf unten.
  //
  // Er läuft nicht im Sammelspeichern mit: `tuerSpeichern` verwirft ihn, weil
  // er ein geschützter Feldschlüssel ist. Und er ist auch fachlich etwas
  // anderes - an ihm hängen Vorlage und elektrische Anforderungen. Ein Typ,
  // der erst beim Speichern greift, hätte die Karte darüber inzwischen
  // widersprochen.
  const [typLaeuft, setTypLaeuft] = useState(false);
  const [typFehler, setTypFehler] = useState<string | null>(null);

  const tuertypSetzen = async (schluessel: string) => {
    setTypLaeuft(true);
    setTypFehler(null);
    try {
      await api.tuerTuertypZuweisen(tuer.id, schluessel || null);
      await neuLaden();
    } catch (e) {
      setTypFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setTypLaeuft(false);
    }
  };

  const speichern = async () => {
    setLaeuft(true);
    setFehler(null);
    try {
      await api.tuerSpeichern(tuer.id, werte);
      setGespeichert(true);
      setTimeout(() => setGespeichert(false), 2500);
      neuLaden();
    } catch (e) {
      setFehler(e);
    } finally {
      setLaeuft(false);
    }
  };


  return (
    <div>
      <div className="karte" style={{ position: 'sticky', top: 16, zIndex: 2 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 14, flexWrap: 'wrap' }}>
          <h1 style={{ fontSize: 21, fontWeight: 300, margin: 0 }}>
            {tuer.tuernummer ?? 'ohne Nummer'}
          </h1>

          {tuer.tuertyp && <StatusBadge zustand="neutral" text={tuer.tuertyp} klein />}

          <span className="sehr-schwach">
            {tuer.raumname ?? tuer.raumnummer ?? 'ohne Raum'}
            {tuer.geschoss ? ` · ${tuer.geschoss}` : ''}
            {tuer.geschossquelle && (
              <span title="Quelle der Geschosszuordnung"> · {tuer.geschossquelle}</span>
            )}
            {/* Die Revit-Ebene daneben, weil sie im Modell steht und bei einer
                Rückfrage gebraucht wird. */}
            {tuer.ebene && tuer.ebene !== tuer.geschoss && (
              <span title="Ebene im Revit-Modell"> ({tuer.ebene})</span>
            )}
          </span>

          {tuer.geschosspruefung && (
            <StatusBadge zustand="warnung" text="Geschoss prüfen" klein
                         titel={tuer.geschosspruefung} />
          )}

          {tuer.state === 'neu' && (
            <StatusBadge zustand="info" text={tuerState(tuer.state)} klein />
          )}

          {!tuer.arcDoorId && (
            <StatusBadge zustand="warnung" text="ohne ARC-Bezug" klein
                         titel="Keine Kennung der Architekturtür" />
          )}

          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
            {gespeichert && <StatusBadge zustand="gut" text="gespeichert" klein />}

            <button className="m-knopf" onClick={() => setVorlagenDialog(true)}
                    disabled={laeuft} title="Werte aus einem Türtyp übernehmen">
              <IconTuertyp size={16} />Vorlage anwenden
            </button>

            <button className="m-knopf m-knopf-haupt" onClick={speichern}
                    disabled={!geaendert || laeuft}>
              {laeuft ? 'einen Moment …' : 'Speichern'}
            </button>
          </div>
        </div>

        {fehler !== null && (
          <ErrorState klein meldung="Die Tür konnte nicht gespeichert werden."
                      technisch={fehler} />
        )}

        <Architektenkennung tuer={tuer} />

        <Raumlage tuer={tuer} />

        <Sicherheitsanforderungen api={api} tuerId={tuer.id} />

        {!vorlage && tuer.tuertyp && (
          <div className="hinweis warnung" style={{ marginTop: 10 }}>
            <span className="hinweis-zeichen">!</span>
            <div>
              Für den Typ <strong>{tuer.tuertyp}</strong> gibt es keine Vorlage.
              Unter „Türtypen" lässt sie sich anlegen.
            </div>
          </div>
        )}

        {vorlagenDialog && (
          <VorlagenDialog
            api={api}
            projektId={projektId}
            tuer={tuer}
            vorlagen={vorlagen}
            felder={felder}
            vorgewaehlt={vorlage?.id}
            schliessen={() => setVorlagenDialog(false)}
            fertig={async () => { setVorlagenDialog(false); await neuLaden(); }}
          />
        )}
      </div>

      <Legende />

      <ElektrischeAnforderungen tuer={tuer} />

      {tuer.feldpflege && <Feldpflegestatus tuer={tuer} />}

      {gruppen.map(([gruppe, gruppenfelder]) => (
        <ContentSection
          key={gruppe}
          titel={gruppe}
          werkzeuge={<GruppenIcon gruppe={gruppe} />}
          aufklappbar
        >
          <div className="m-feldgitter m-feldgitter-3">
            {gruppenfelder.map((f) => (
              <Feld
                key={f.id}
                feld={f}
                wert={werte[f.fieldKey] ?? ''}
                setzen={(v) => setWerte({ ...werte, [f.fieldKey]: v || null })}
                tuertypen={vorlagen}
                tuertypSetzen={tuertypSetzen}
                tuertypLaeuft={typLaeuft}
                tuertypFehler={typFehler}
              />
            ))}
          </div>
        </ContentSection>
      ))}

      <p className="sehr-schwach" style={{ textAlign: 'right' }}>
        Aufgenommen {new Date(tuer.firstSeenAt).toLocaleDateString('de-DE')}
        {tuer.updatedAt && `, gepflegt ${new Date(tuer.updatedAt).toLocaleDateString('de-DE')}`}
        {tuer.updatedBy && ` von ${tuer.updatedBy}`}
      </p>
    </div>
  );
}

function Feldpflegestatus({ tuer }: { tuer: TuerDetail }) {
  const p = tuer.feldpflege!;
  const aktualisiert: Record<string, unknown> = JSON.parse(p.aktualisierteFelderJson || '{}');
  return (
    <ContentSection titel="Feldpflege-Nachweis">
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <StatusBadge
          klein
          zustand={p.revitVerknuepfungsstatus === 'unveraendert' ? 'gut' : 'warnung'}
          text={`Revit-Verknüpfung ${p.revitVerknuepfungsstatus}`}
        />
        <StatusBadge zustand="neutral" text={p.anforderungsaktion} klein />
        <StatusBadge
          klein zustand="neutral"
          text={`${Object.keys(aktualisiert).length} Feldvorgaben`}
        />
      </div>
      <p style={{ marginBottom: 0, fontSize: 13 }}>
        Türtyp: {p.tuertypVorher ?? 'nicht festgelegt'} → {p.tuertypFinal ?? 'nicht festgelegt'}
      </p>
      {p.offenePruefposten && (
        <div className="hinweis warnung" style={{ marginTop: 10 }}>
          <span className="hinweis-zeichen">!</span><div>{p.offenePruefposten}</div>
        </div>
      )}
    </ContentSection>
  );
}

/**
 * Der auswertbare Planinhalt am gewaehlten Tuerexemplar. Die Anforderungen sind
 * nur lesbar: Geaendert wird eine Planrevision am Tuertyp, nicht als Freitext an
 * einer einzelnen Tuer.
 */
function ElektrischeAnforderungen({ tuer }: { tuer: TuerDetail }) {
  const z = tuer.anforderungszuordnung;
  const status = z?.status ?? 'pruefen';

  const spalten: Spalte<TuerDetail['anforderungen'][number]>[] = [
    { schluessel: 'kategorie', titel: 'Kategorie', wert: (a) => a.kategorie },
    {
      schluessel: 'komponente', titel: 'Komponente', immerSichtbar: true,
      wert: (a) => a.komponente,
    },
    { schluessel: 'menge', titel: 'Menge', art: 'zahl', breite: '90px', wert: (a) => a.menge },
    { schluessel: 'pflicht', titel: 'Pflicht', wert: (a) => a.pflichtstatus },
    { schluessel: 'system', titel: 'System', wert: (a) => a.system },
    {
      schluessel: 'bemerkung', titel: 'Bemerkung',
      wert: (a) => a.bemerkung ?? '',
      inhalt: (a) => a.bemerkung ?? '–',
    },
  ];

  return (
    <ContentSection
      titel="Elektrische Anforderungen"
      beschreibung={z?.grundlage
        ?? 'Für diese Tür liegt noch keine Anforderungszuordnung vor.'}
      meta={z && (
        <>
          {z.quelldokument} · Planstand {z.planstand} vom{' '}
          {new Date(`${z.quelldatum}T00:00:00`).toLocaleDateString('de-DE')}
        </>
      )}
      werkzeuge={(
        <>
          <StatusBadge
            klein
            zustand={status === 'zugeordnet' ? 'gut'
              : status === 'pruefen' ? 'warnung' : 'neutral'}
            text={status === 'zugeordnet' ? 'eindeutig zugeordnet'
              : status === 'pruefen' ? 'Zuordnung prüfen'
                : 'nicht in diesem Planstand'}
          />
          {z?.anforderungscode && <code>{z.anforderungscode}</code>}
        </>
      )}
    >
      <DataTable
        spalten={spalten}
        zeilen={tuer.anforderungen}
        schluessel={(a) => a.id}
        suche={tuer.anforderungen.length > 8}
        spaltenwahl={false}
        dicht
        leer={(
          <EmptyState
            klein
            titel="Keine Anforderungen angelegt."
            erklaerung={'Bei Status „prüfen" ist zuerst die eindeutige Typzuordnung zu '
              + 'klären.'}
          />
        )}
      />

      {z && (
        <p className="sehr-schwach" style={{ margin: '12px 0 0' }}>
          Zuordnung Revision {z.revision} ({z.zuordnungsart})
        </p>
      )}
    </ContentSection>
  );
}

function Feld({
  feld, wert, setzen, tuertypen,
  tuertypSetzen, tuertypLaeuft, tuertypFehler,
}: {
  feld: Tuerfeld;
  wert: string;
  setzen: (v: string) => void;
  /**
   * Die Türtypen, für das Feld `tuertyp`. Sie kommen aus door_templates und
   * nicht aus `feld.options` - eine zweite Liste in den Feldstammdaten wäre
   * eine zweite Wahrheit, und sie ginge auseinander, sobald ein Typ dazukommt.
   */
  tuertypen?: Tuertyp[];

  /** Speichert die Wahl sofort über den eigenen Weg. */
  tuertypSetzen?: (schluessel: string) => void | Promise<void>;
  tuertypLaeuft?: boolean;
  tuertypFehler?: string | null;
}) {
  // ══════════════════════════════════════════════════════════════════════
  // DIE FARBE SAGT, OB DER WERT INS MODELL ZURUECKGEHT
  // ══════════════════════════════════════════════════════════════════════
  //
  // Hier stand bis PB52 `feld.revitParameter ? 'modellfeld' : 'datenfeld'`.
  // Seit Migration 0729 (Festlegung F61: „wir können ins revit modell der
  // architekten nicht schreiben") geht KEIN Türfeld mehr zurück - die 17
  // Felder mit Parameter erschienen trotzdem weiter hellgrün. Die Seite
  // konnte es nicht besser wissen: Die Antwort stand in keiner Auskunft,
  // die sie holen kann.
  //
  // Jetzt kommt sie fertig aus dem Rechenkern
  // (Rueckschreibweg.GehtInsModell) und wird hier nur noch in eine Farbe
  // übersetzt.
  const art = farbklasse(feld.gehtInsModell);

  // Der Tooltip sagte an jedem nicht-grünen Feld „Nur im Türbuch - wird
  // nicht zurückgeschrieben" und behauptete damit im Umkehrschluss das
  // Gegenteil für die grünen. Beides war falsch, sobald ein Feld einen
  // Parameter trägt, aus dem nur GELESEN wird: Der Wert steht dann sehr
  // wohl im Modell, er wandert nur nicht dorthin zurück.
  //
  // Deshalb drei Fälle statt zwei - und jeder sagt, was er meint.
  const hinweis = feld.gehtInsModell
    ? `${feld.revitParameter} — wird ins Modell zurückgeschrieben`
    : feld.revitParameter
      ? `${feld.revitParameter} — wird nur gelesen, nicht zurückgeschrieben`
      : 'Nur im Türbuch - kein Parameter im Modell hinterlegt';
  const beschriftungstext = feld.unit ? `${feld.label} [${feld.unit}]` : feld.label;

  const beschriftung = (
    <label title={hinweis} style={{ fontSize: 12, color: 'var(--text-schwach)' }}>
      {feld.label}
      {feld.unit && <span className="sehr-schwach"> [{feld.unit}]</span>}
    </label>
  );

  // ══════════════════════════════════════════════════════════════════════
  // DER TÜRTYP IST EINE AUSWAHL, KEIN FREITEXT
  // ══════════════════════════════════════════════════════════════════════
  //
  // Er stand als `text` in den Feldstammdaten, also als Eingabezeile. Damit
  // entscheidet die Schreibweise über die Zuordnung: Der Typ ist der
  // Schlüssel, über den `door_templates` die Ausstattung und die
  // elektrischen Anforderungen liefert. Ein Leerzeichen zu viel, und die Tür
  // hat keine Vorlage mehr - sichtbar erst unten in der Karte, als
  // „Für den Typ … gibt es keine Vorlage".
  //
  // Die Liste kommt aus den Türtypen selbst, nicht aus `feld.options`.
  //
  // WICHTIG: Der aktuelle Wert bleibt wählbar, auch wenn er nicht in der
  // Liste steht. Sonst zeigte das Feld beim Öffnen „nicht festgelegt" und
  // das Speichern löschte den Typ - ohne dass jemand ihn angefasst hätte.
  if (feld.fieldKey === 'tuertyp' && tuertypen && tuertypen.length > 0) {
    const aktive = tuertypen
      .filter((t) => t.isActive !== false)
      .slice()
      .sort((a, b) => a.templateKey.localeCompare(b.templateKey, 'de'));

    const vorhanden = tuertypen.find((t) => t.templateKey === wert);
    const fehltInListe = wert !== ''
      && !aktive.some((t) => t.templateKey === wert);

    return (
      <div
        className={art}
        style={{ display: 'grid', gridTemplateColumns: '160px 1fr',
                 gap: 8, alignItems: 'center', gridColumn: '1 / -1' }}
      >
        {beschriftung}
        <div>
          <select
            value={wert}
            disabled={tuertypLaeuft}
            onChange={(e) => (tuertypSetzen ?? setzen)(e.target.value)}
            style={{ width: '100%' }}
          >
            <option value="">nicht festgelegt</option>

          {fehltInListe && (
            <option value={wert}>
              {wert}
              {vorhanden
                ? ` – ${vorhanden.name} (stillgelegt)`
                : ' (nicht in der Typenliste)'}
            </option>
          )}

          {aktive.map((t) => (
            <option key={t.templateKey} value={t.templateKey}>
              {t.templateKey} – {t.name}
            </option>
          ))}
          </select>

          {/* Die Wahl greift sofort - das gehört dahin, wo sie getroffen wird,
              sonst sucht man den Speicherknopf. */}
          <div style={{ fontSize: 11, marginTop: 3,
                        color: tuertypFehler ? 'var(--fehler)'
                                             : 'var(--text-schwach)' }}>
            {tuertypFehler
              ?? (tuertypLaeuft
                ? 'wird zugewiesen …'
                : 'Die Wahl wird sofort gespeichert. Ins Modell kommt sie mit '
                  + 'der nächsten Rücklieferung.')}
          </div>
        </div>
      </div>
    );
  }

  if (feld.dataType === 'ja_nein') {
    return (
      <div className={art}>
        <MusterFeld
          art="auswahl"
          beschriftung={beschriftungstext}
          hinweis={hinweis}
          wert={wert}
          beiAenderung={(w) => setzen(w as string)}
          optionen={[
            { wert: '', beschriftung: '—' },
            { wert: 'TRUE', beschriftung: jaNein('TRUE') },
            { wert: 'FALSE', beschriftung: jaNein('FALSE') },
          ]}
        />
      </div>
    );
  }

  if (feld.dataType === 'auswahl' && feld.options) {
    return (
      <div className={art}>
        <MusterFeld
          art="auswahl"
          beschriftung={beschriftungstext}
          hinweis={hinweis}
          wert={wert}
          beiAenderung={(w) => setzen(w as string)}
          optionen={[
            { wert: '', beschriftung: '—' },
            ...feld.options.split(',').map((o) => ({
              wert: o.trim(), beschriftung: o.trim(),
            })),
          ]}
        />
      </div>
    );
  }

  /* Abschnitt 15: Wert und Einheit getrennt. Gespeichert bleibt ein Text;
     `leseZahl`/`formatiereZahl` übersetzen an der Grenze. */
  if (feld.dataType === 'zahl') {
    return (
      <div className={art}>
        <MusterFeld
          art="zahl"
          beschriftung={feld.label}
          einheit={feld.unit ?? undefined}
          hinweis={hinweis}
          wert={leseZahl(wert)}
          beiAenderung={(w) => setzen(w === null ? '' : formatiereZahl(w as number))}
        />
      </div>
    );
  }

  return (
    <div className={art}>
      <MusterFeld
        beschriftung={beschriftungstext}
        hinweis={hinweis}
        wert={wert}
        beiAenderung={(w) => setzen(w as string)}
      />
    </div>
  );
}

/**
 * Wählt einen Türtyp und überträgt seine Werte.
 *
 * Vorgewählt ist der Typ, der im Modell steht - meist der richtige. Ein anderer
 * ist möglich: Wenn im Modell noch „tbd" steht, die Ausstattung aber schon
 * feststeht, hilft es, die Werte trotzdem zu setzen.
 */
function VorlagenDialog({
  api, projektId, tuer, vorlagen, felder, vorgewaehlt, schliessen, fertig,
}: {
  api: ApiClient;
  projektId: string;
  tuer: TuerDetail;
  vorlagen: Tuertyp[];
  felder: Tuerfeld[];
  vorgewaehlt?: string;
  schliessen: () => void;
  fertig: () => void | Promise<void>;
}) {
  const [gewaehlt, setGewaehlt] = useState(vorgewaehlt ?? '');
  const [ueberschreiben, setUeberschreiben] = useState(false);
  const [fehler, setFehler] = useState<unknown>(null);
  const [laeuft, setLaeuft] = useState(false);

  const vorlage = vorlagen.find((v) => v.id === gewaehlt);

  // Was die Vorlage vorgibt, mit Beschriftung statt Feldschlüssel.
  const vorgaben = useMemo(() => {
    if (!vorlage?.dataJson) return [];

    const werte: Record<string, string | null> = JSON.parse(vorlage.dataJson);
    const vorhanden: Record<string, string | null> =
      tuer.dataJson ? JSON.parse(tuer.dataJson) : {};

    return Object.entries(werte)
      .filter(([, wert]) => wert && wert.trim())
      .map(([schluessel, wert]) => {
        const feld = felder.find((f) => f.fieldKey === schluessel);
        const bisher = vorhanden[schluessel];

        return {
          schluessel,
          label: feld?.label ?? schluessel,
          gruppe: feld?.gruppe ?? '',
          neu: jaNein(wert),
          bisher: bisher == null ? bisher : jaNein(bisher),
          kollision: !!bisher && bisher.trim() !== '',
        };
      });
  }, [vorlage, tuer, felder]);

  const kollisionen = vorgaben.filter((v) => v.kollision).length;
  const uebertragbar = ueberschreiben ? vorgaben.length : vorgaben.length - kollisionen;

  const anwenden = async () => {
    if (!vorlage) return;

    setLaeuft(true);
    setFehler(null);
    try {
      await api.tuerVorlageZuweisen(projektId, {
        vorlageId: vorlage.id,
        tuerIds: [tuer.id],
        ueberschreiben,
      });
      await fertig();
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
      setLaeuft(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,.35)', zIndex: 30,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
    }}>
      <div className="karte" style={{ width: 620, maxHeight: '85vh',
                                      overflowY: 'auto', margin: 0 }}>
        <h2 className="karte-titel"><IconTuertyp />Vorlage anwenden</h2>

        <p className="sehr-schwach" style={{ marginTop: 0 }}>
          Tür <strong>{tuer.tuernummer}</strong>
          {tuer.tuertyp && <> · im Modell steht <strong>{tuer.tuertyp}</strong></>}
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: '90px 1fr',
                      gap: 10, alignItems: 'center', marginTop: 14 }}>
          <label>Türtyp</label>
          <select value={gewaehlt} onChange={(e) => setGewaehlt(e.target.value)}>
            <option value="">— wählen —</option>
            {vorlagen.map((v) => {
              const anzahl = v.dataJson
                ? Object.values(JSON.parse(v.dataJson)).filter((x) => x).length
                : 0;

              return (
                <option key={v.id} value={v.id}>
                  {v.templateKey} · {v.name}
                  {anzahl === 0 ? ' (ohne Werte)' : ` (${anzahl} Werte)`}
                </option>
              );
            })}
          </select>
        </div>

        {fehler !== null && (
          <ErrorState klein meldung="Die Vorlage konnte nicht angewendet werden."
                      technisch={fehler} />
        )}

        {vorlage && vorgaben.length === 0 && (
          <div className="hinweis warnung" style={{ marginTop: 14 }}>
            <span className="hinweis-zeichen">!</span>
            <div>
              <strong>{vorlage.name}</strong> hat noch keine Werte. Unter
              „Türtypen" lassen sie sich pflegen - danach greift die Vorlage für
              alle Türen dieses Typs.
            </div>
          </div>
        )}

        {vorgaben.length > 0 && (
          <>
            <DataTable
              name="Was die Vorlage setzt"
              spalten={[
                {
                  schluessel: 'feld', titel: 'Feld', immerSichtbar: true,
                  wert: (v: typeof vorgaben[number]) => v.label,
                  inhalt: (v) => (
                    <>
                      {v.label}
                      {v.gruppe && <span className="sehr-schwach"> · {v.gruppe}</span>}
                    </>
                  ),
                },
                {
                  schluessel: 'bisher', titel: 'bisher',
                  wert: (v: typeof vorgaben[number]) => v.bisher ?? '',
                  inhalt: (v) => <span className="sehr-schwach">{v.bisher ?? '—'}</span>,
                },
                {
                  schluessel: 'wird', titel: 'wird', art: 'status', breite: '200px',
                  wert: (v: typeof vorgaben[number]) =>
                    (v.kollision && !ueberschreiben ? 'bleibt' : v.neu ?? ''),
                  inhalt: (v) => (v.kollision && !ueberschreiben
                    ? (
                      <StatusBadge
                        zustand="neutral" text="bleibt" klein
                        titel="Der vorhandene Wert wird nicht überschrieben."
                      />
                    )
                    : <strong>{v.neu}</strong>),
                },
              ]}
              zeilen={vorgaben}
              schluessel={(v) => v.schluessel}
              suche={vorgaben.length > 8}
              spaltenwahl={false}
              hoehe={360}
              dicht
            />

            {kollisionen > 0 && (
              <label style={{ display: 'flex', alignItems: 'center', gap: 8,
                              marginTop: 12, fontSize: 13 }}>
                <input
                  type="checkbox"
                  checked={ueberschreiben}
                  onChange={(e) => setUeberschreiben(e.target.checked)}
                />
                Gepflegte Werte überschreiben
                <span className="sehr-schwach">
                  ({kollisionen} {kollisionen === 1 ? 'Feld' : 'Felder'} betroffen)
                </span>
              </label>
            )}
          </>
        )}

        <div style={{ marginTop: 16, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="m-knopf" onClick={schliessen} disabled={laeuft}>
            Abbrechen
          </button>
          <button
            className="m-knopf m-knopf-haupt"
            onClick={anwenden}
            disabled={laeuft || uebertragbar === 0}
          >
            {laeuft
              ? 'wird übertragen …'
              : uebertragbar === 0
                ? 'nichts zu übertragen'
                : `${uebertragbar} ${uebertragbar === 1 ? 'Wert' : 'Werte'} übertragen`}
          </button>
        </div>
      </div>
    </div>
  );
}

// --- Lage und verbundene Räume ---------------------------------------------

/**
 * Welche Räume die Tür verbindet und wo sie liegt.
 *
 * Ergänzend, nicht ersetzend: Die führende Zuordnung bleibt die über die
 * Türnummer aus dRofus — E0-008-1 gehört zu Raum E0-008. Diese Anzeige sagt
 * zusätzlich, was die Tür tatsächlich verbindet, und deckt damit Fälle auf, in
 * denen Nummer und Lage auseinanderlaufen.
 *
 * Liegt die Tür innerhalb eines Raums — etwa in einer Trennwand im Großraum —,
 * zählt nur der eine Raum.
 */
/**
 * Die Sicherheitsanforderungen einer Tür, mit Regel und Begründung.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * WARUM ERST BEIM ÖFFNEN GELADEN
 * ══════════════════════════════════════════════════════════════════════════
 *
 * Weil es projektweit 24.811 sind. Die Türliste lädt 1.845 Türen; nähme sie die
 * Anforderungen mit, wüchse die Antwort um das Fünfzehnfache. Das Türbuch hat
 * dieses Problem schon einmal gehabt — deshalb dasselbe Muster wie bei den
 * Türwerten: Details on demand.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * WARUM DIE REGEL DANEBEN STEHT
 * ══════════════════════════════════════════════════════════════════════════
 *
 * Weil „R06" allein keine Begründung ist. Wer eine Tür aufmacht und fragt, warum
 * hier eine Kamera hängt, braucht drei Dinge: das Merkmal, die Regel und die
 * Seite im Konzept. Erst dann ist es ein Nachweis und keine Behauptung.
 */
function Sicherheitsanforderungen({
  api, tuerId,
}: {
  api: ApiClient;
  tuerId: string;
}) {
  const [daten, setDaten] = useState<TuerSicherheitDetail | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<unknown>(null);
  const [offen, setOffen] = useState(false);

  useEffect(() => {
    let abgebrochen = false;

    setLaeuft(true);
    api.tuerSicherheit(tuerId)
      .then((d) => { if (!abgebrochen) setDaten(d); })
      .catch((e) => { if (!abgebrochen) setFehler(e); })
      .finally(() => { if (!abgebrochen) setLaeuft(false); });

    return () => { abgebrochen = true; };
  }, [api, tuerId]);

  if (laeuft && !daten) {
    return <LoadingState text="Sicherheitsanforderungen werden geladen …" zeilen={3} />;
  }

  if (fehler !== null) {
    return (
      <ErrorState
        klein
        meldung="Die Sicherheitsanforderungen konnten nicht geladen werden."
        technisch={fehler}
      />
    );
  }

  if (!daten || daten.anforderungen.length === 0) {
    return (
      <EmptyState
        klein
        titel="Keine Sicherheitsanforderungen abgeleitet."
        erklaerung={'Sie entstehen, wenn die Schutzzonen der angrenzenden Räume im '
          + 'Raumbuch gepflegt sind und die Ableitung gelaufen ist.'}
      />
    );
  }

  const t = daten.tuer;

  // Die Marke zum Status. Der schlechteste gewinnt, deshalb steht er am Kopf.
  //
  // Vier Farben für vier Stufen (Status.Rang): bestätigt ist der beste Fall,
  // Annahme ein akzeptierter Kompromiss, fehlt und Klärung offen sind die
  // beiden Fälle, denen ein Planer nachgehen muss - "fehlt" bekommt seine
  // eigene (rote) Farbe statt in derselben Marke wie "Klärung offen" zu
  // verschwinden.
  const statuszustand = (status: string): Zustand => {
    if (status === 'verified') return 'gut';
    if (status === 'assumption') return 'info';
    if (status === 'missing') return 'fehler';
    return 'warnung';
  };

  // Sichtbar sind zunächst die Merkmale, die etwas VERLANGEN, sowie alles
  // Klärungsbedürftige. Die Feststellungen (Räume, Zonen, Stufen) stehen schon
  // im Lagekasten darüber — hier wären sie Wiederholung.
  const feststellungen = new Set([
    'raum_bandseite', 'raum_bandgegenseite',
    'schutzzone_bandseite', 'schutzzone_bandgegenseite',
    'schutzstufe_bandseite', 'schutzstufe_bandgegenseite',
  ]);

  const sichtbar = offen
    ? daten.anforderungen
    : daten.anforderungen.filter((a) => !feststellungen.has(a.merkmal));

  return (
    <div className="modellfeld" style={{ marginTop: 12 }}>
      <div className="feldname" style={{ marginBottom: 6 }}>
        Sicherheitskonzept
        {t.tuerklasse && (
          <span className="reiter-marke" title={daten.tuerklasse?.bezeichnung}>
            {t.tuerklasse}
          </span>
        )}
        {t.sicherheitStatus && (
          <> <StatusBadge
            klein
            zustand={statuszustand(t.sicherheitStatus)}
            text={sicherheitStatus(t.sicherheitStatus)}
          /></>
        )}
        {t.istZonenwechsel === true && (
          <> <StatusBadge
            klein zustand="warnung"
            text={`Zonenwechsel ${t.schutzzoneBandseite} → ${t.schutzzoneBandgegenseite}`}
            titel={`Sicherungsseite: ${sicherungsseite(t.sicherungsseite)}`}
          /></>
        )}
      </div>

      {!t.seitenSindGeometrisch && (
        <p className="sehr-schwach" style={{ fontSize: 12, margin: '0 0 8px' }}>
          Die Seitenzuordnung stammt aus der Ringabtastung und trägt keine
          Richtung — welcher Raum auf der Bandseite liegt, ist nicht belegt.
          Seitenbezogene Anforderungen (Panik-Beschlag, Sicherungsseite) sind
          entsprechend zu prüfen.
        </p>
      )}

      <DataTable
        spalten={[
          {
            schluessel: 'merkmal', titel: 'Merkmal', immerSichtbar: true,
            breite: '220px',
            wert: (a: TuerSicherheitDetail['anforderungen'][number]) => merkmal(a.merkmal),
          },
          {
            schluessel: 'wert', titel: 'Wert',
            wert: (a: TuerSicherheitDetail['anforderungen'][number]) => a.wert ?? '',
            inhalt: (a) => (a.wert && a.wert.length > 0
              ? a.wert
              : <span className="sehr-schwach">—</span>),
          },
          {
            schluessel: 'regel', titel: 'Regel', breite: '130px',
            wert: (a: TuerSicherheitDetail['anforderungen'][number]) => a.regelId,
            inhalt: (a) => (
              <span title={`${a.regelgegenstand}: ${a.regelwirkung}`
                + (a.regelquelle ? ` (${a.regelquelle})` : '')}>
                {a.regelId}
              </span>
            ),
          },
          {
            schluessel: 'status', titel: 'Status', art: 'status', breite: '230px',
            wert: (a: TuerSicherheitDetail['anforderungen'][number]) =>
              sicherheitStatus(a.status),
            inhalt: (a) => (
              <>
                <StatusBadge
                  klein
                  zustand={statuszustand(a.status)}
                  text={sicherheitStatus(a.status)}
                  titel={a.begruendung ?? undefined}
                />
                {a.offenerPunkt && (
                  <> <StatusBadge zustand="warnung" text={a.offenerPunkt} klein /></>
                )}
              </>
            ),
          },
        ]}
        zeilen={sichtbar}
        schluessel={(a) => a.merkmal}
        suche={sichtbar.length > 8}
        spaltenwahl={false}
        dicht
        leer={(
          <EmptyState
            klein
            titel="Keine Anforderung sichtbar."
            erklaerung="Die Feststellungen lassen sich unten einblenden."
          />
        )}
      />

      <button
        className="m-knopf"
        onClick={() => setOffen(!offen)}
        style={{ marginTop: 8 }}
      >
        {offen
          ? 'Feststellungen ausblenden'
          : `Feststellungen einblenden (${
              daten.anforderungen.length - sichtbar.length})`}
      </button>

      {daten.offenePunkte.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <div className="feldname" style={{ marginBottom: 4 }}>
            Offene Punkte
          </div>
          {daten.offenePunkte.map((o) => (
            <div key={o.nummer} className="hinweis warnung"
                 style={{ marginBottom: 6 }}>
              <span className="hinweis-zeichen">!</span>
              <div>
                <strong>{o.nummer}</strong> {o.gegenstand}
                {o.sachverhalt && (
                  <div style={{ fontSize: 12, marginTop: 2 }}>{o.sachverhalt}</div>
                )}
                {o.zustaendig && (
                  <div className="sehr-schwach" style={{ fontSize: 12 }}>
                    zuständig: {o.zustaendig}
                    {o.quelle && ` · ${o.quelle}`}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {t.sicherheitGerechnetAm && (
        <p className="sehr-schwach" style={{ fontSize: 12, marginTop: 8,
                                             marginBottom: 0 }}>
          Abgeleitet am{' '}
          {new Date(t.sicherheitGerechnetAm).toLocaleString('de-DE')}
          {t.pruefabstandMm != null
            && ` · Prüfabstand der Seitenerkennung ${
                 Math.round(t.pruefabstandMm)} mm`}
        </p>
      )}
    </div>
  );
}

/**
 * Die Kennung, die der Architekt vergeben hat — TUER_ID.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * HELLGRÜN UND TROTZDEM NICHT BEARBEITBAR
 * ══════════════════════════════════════════════════════════════════════════
 *
 * `modellfeld` ist hellgrün und heißt im Türbuch bisher: „kommt aus dem Modell
 * und wird dorthin zurückgeschrieben". Hier gilt nur die erste Hälfte. Der Wert
 * kommt aus dem ARCHITEKTURmodell und geht nie zurück — er gehört dem
 * Architekten.
 *
 * Deshalb kein Eingabefeld, sondern Text mit Schloss. Ein Eingabefeld, das man
 * beschreiben kann und dessen Wert beim nächsten Import wieder dasteht, ist
 * schlimmer als keins: Es sieht aus wie eine Zusage.
 */
function Architektenkennung({ tuer }: { tuer: TuerDetail }) {
  // Am TYP gefunden ist ein Warnzeichen: Dann ist die Kennung für alle Türen
  // dieses Typs dieselbe und taugt nicht zum Unterscheiden.
  const amTyp = tuer.arcTuerIdQuelle?.includes('(Typ)') ?? false;

  return (
    <div className="modellfeld" style={{ marginTop: 12 }}>
      <div className="feldname" style={{ marginBottom: 6 }}>
        Architektenkennung
        <span className="reiter-marke"
              title="Wird nur gelesen und nie ins Architekturmodell zurückgeschrieben">
          fest
        </span>
      </div>

      <div className="modellwerte">
        <div>
          <div className="feldname">TUER_ID</div>
          <div style={{ fontSize: 13, fontFamily: 'var(--schrift-fest, monospace)' }}>
            {tuer.arcTuerId ?? '—'}
          </div>
        </div>

        <div>
          <div className="feldname">Parameter im Modell</div>
          <div style={{ fontSize: 13 }}>
            {tuer.arcTuerIdQuelle ?? '—'}
            {amTyp && (
              <> <StatusBadge
                klein zustand="warnung" text="am Typ"
                titel={'Am Typ gepflegt, nicht am Exemplar — der Wert ist für alle '
                  + 'Türen dieses Typs derselbe und taugt dann nicht als Kennung.'}
              /></>
            )}
          </div>
        </div>

        <div>
          <div className="feldname">Bezug zur Architekturtür</div>
          <div style={{ fontSize: 13 }}>
            {tuer.arcDoorId ?? '—'}
            {tuer.arcLinkModel && (
              <span className="sehr-schwach"> · {tuer.arcLinkModel}</span>
            )}
          </div>
        </div>
      </div>

      {!tuer.arcTuerId && (
        <p className="sehr-schwach" style={{ fontSize: 12, marginTop: 8, marginBottom: 0 }}>
          {tuer.arcDoorId
            ? 'Die Architekturtür ist zugeordnet, trägt aber keinen Parameter '
              + 'TUER_ID (auch nicht TÜR_ID). Entweder heißt er anders, oder er '
              + 'ist im Architekturmodell noch nicht vergeben.'
            : 'Ohne zugeordnete Architekturtür ist die Kennung nicht erreichbar. '
              + 'Sie entsteht bei der nächsten Türaufnahme.'}
        </p>
      )}
    </div>
  );
}

/**
 * Die Marke zur Zonenbewertung.
 *
 * Vier Zustände, und der Unterschied zwischen dem dritten und den ersten zwei
 * ist der Punkt: Ein Übergang ist zutrittsrelevant, „innerhalb" ist es nicht,
 * und „unbestimmt" ist WEDER das eine noch das andere — es ist eine offene
 * Frage. Sie als „innerhalb" darzustellen versteckt sie hinter einer Zahl.
 */
function Zonenmarke({ tuer }: { tuer: TuerDetail }) {
  if (tuer.zonenbewertung === 'uebergang') {
    return (
      <StatusBadge
        klein zustand="warnung" text="Zonenübergang"
        titel={'Zonenübergang — hier wird der Zutritt geregelt. '
          + `Paar: ${tuer.zonenpaar ?? ''}`}
      />
    );
  }

  if (tuer.zonenbewertung === 'innerhalb') {
    return (
      <StatusBadge
        klein zustand="neutral" text="innerhalb"
        titel="Beide Räume in derselben Zone — kein Zutrittsübergang"
      />
    );
  }

  if (tuer.zonenbewertung === 'unbestimmt') {
    return (
      <StatusBadge
        klein zustand="warnung" text="Zone offen"
        titel={tuer.zonenpruefung ?? 'Zone noch nicht entschieden'}
      />
    );
  }

  return null;
}

function Raumlage({ tuer }: { tuer: TuerDetail }) {
  // Auf null UND undefined pruefen: Die Antwort laesst leere Felder weg, und
  // 'undefined !== null' ist wahr - der Kasten erschien dann mit leerem Inhalt,
  // statt zu sagen, dass es keine Auswertung gibt.
  const hatLage = tuer.posX != null;
  const hatRaeume = tuer.vonRaumnummer != null || tuer.nachRaumnummer != null;

  if (!hatLage && !hatRaeume) {
    return (
      <p className="sehr-schwach" style={{ fontSize: 12, marginBottom: 0, marginTop: 10 }}>
        Keine Lageauswertung. Sie entsteht bei der nächsten Türaufnahme, wenn ein
        Modell mit Räumen verknüpft ist.
      </p>
    );
  }

  // 'geometrie_innen' ist der Name aus einer älteren Fassung der Ableitung
  // und steht hier nur noch als Rückfallebene für Altbestände - aktuell
  // schreibt die Aufnahme 'arc_innen'/'ring_innen'.
  const innen = tuer.raumVerfahren === 'geometrie_innen'
             || tuer.raumVerfahren === 'arc_innen'
             || tuer.raumVerfahren === 'ring_innen';

  // Klartext aus EINER Stelle (tuerKlartext.ts) - er sagt nicht nur WOMIT
  // (Architekturtür/Ringsuche), sondern auch WIE (beidseitig/innenliegend,
  // eindeutig/mehrdeutig). Der Tooltip ergänzt das um die Einschränkung bei
  // 'ring_mehrdeutig', die 800 von 1.852 Türen im Bestand betrifft.
  const verfahrenName = tuer.raumVerfahren ? raumVerfahren(tuer.raumVerfahren) : null;

  // Weicht der Raum aus der Nummer von den erkannten ab, ist das ein Befund:
  // Entweder ist die Nummer falsch oder die Tür sitzt anders als gedacht.
  const erkannt = [tuer.vonRaumnummer, tuer.nachRaumnummer].filter(Boolean);

  const passt = erkannt.length === 0 || !tuer.raumnummer
    || erkannt.includes(tuer.raumnummer);

  return (
    <div className="modellfeld" style={{ marginTop: 12 }}>
      <div className="feldname" style={{ marginBottom: 6 }}>
        Lage im Modell
        {verfahrenName && (
          <span
            className="reiter-marke"
            title={tuer.raumVerfahren === 'ring_mehrdeutig'
              ? 'Mehr als zwei Räume in der Umgebung — etwa an einer Ecke. '
                + 'Die beiden ersten sind genannt.'
              : 'Wie die Räume gefunden wurden'}
          >
            {verfahrenName}
          </span>
        )}
      </div>

      <div className="modellwerte">
        {hatRaeume && (
          <div>
            <div className="feldname">{innen ? 'Liegt in Raum' : 'Verbindet'}</div>
            <div style={{ fontSize: 13 }}>
              {tuer.vonRaumnummer
                ? `${tuer.vonRaumnummer}${tuer.vonRaumname ? ' · ' + tuer.vonRaumname : ''}`
                : '—'}
              {!innen && (
                <>
                  {' ↔ '}
                  {tuer.nachRaumnummer
                    ? `${tuer.nachRaumnummer}${tuer.nachRaumname ? ' · ' + tuer.nachRaumname : ''}`
                    : 'außen oder ohne Raum'}
                </>
              )}
            </div>
          </div>
        )}

        {hatLage && (
          <div>
            <div className="feldname">Lage [mm]</div>
            <div style={{ fontSize: 13 }}>
              {tuer.posX} / {tuer.posY} / {tuer.posZ}
            </div>
          </div>
        )}

        {/* Die Sicherheitszonen beider Seiten.
            Sie stehen HIER und nicht in einem eigenen Kasten: Die Zone ist eine
            Eigenschaft der Räume, die die Tür verbindet, und ohne die Räume
            daneben ist sie nicht zu prüfen. */}
        <div>
          <div className="feldname">Sicherheitszone</div>
          <div style={{ fontSize: 13 }}>
            {tuer.vonSicherheitszone ?? '—'}
            {!innen && (
              <>
                {' ↔ '}
                {tuer.nachSicherheitszone ?? '—'}
              </>
            )}
            <Zonenmarke tuer={tuer} />
          </div>
        </div>
      </div>

      {hatLage && !hatRaeume && (
        <p className="sehr-schwach" style={{ fontSize: 12, marginTop: 8, marginBottom: 0 }}>
          Die Lage ist bekannt, aber an keinem Prüfpunkt lag ein Raum. Im Protokoll
          des Auftrags steht, welche Verlinkungen geladen waren.
        </p>
      )}

      {tuer.zonenpruefung && (
        <p className="sehr-schwach" style={{ fontSize: 12, marginTop: 8, marginBottom: 0 }}>
          {tuer.zonenpruefung}
        </p>
      )}

      {!passt && (
        <div className="hinweis warnung" style={{ marginTop: 10 }}>
          <span className="hinweis-zeichen">!</span>
          <div>
            Die Türnummer zeigt auf <strong>{tuer.raumnummer}</strong>, die Lage aber
            auf {erkannt.join(' und ')}.
            Entweder ist die Nummer nicht mehr aktuell oder die Tür sitzt anders als
            geplant — die Zuordnung im Türbuch folgt weiterhin der Nummer.
          </div>
        </div>
      )}
    </div>
  );
}
